import { Image as SiteImage, RichText, SiteLink } from 'components/shared';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { getLinkHref } from 'lib/utils/helpers';
import { useUI } from 'hooks';
import validateRequired from './validate-required';
import withPropValidation from './with-props-validation.tsx';
import { addNewLines } from './articles/richTextOptions';

const Section = ({ data: { hasImageBackground, article } }) => {
  const { isDesktop } = useUI();
  const { title, image, mobileImage } = article;

  const getTemplateStyle = () => {
    if (hasImageBackground && title) {
      return 'a';
    }

    if (!title) {
      return 'b';
    }

    return 'c';
  };

  const templateStyle = getTemplateStyle();

  const mainHeadingRender = title && (
    <div
      className={`${
        templateStyle === 'a'
          ? 'pb-[360px] pt-[180px] md:pb-[800px] md:pt-[200px]'
          : 'mb-[34px] bg-blue pt-[80px] md:mb-[40px] md:pt-[145px]'
      } relative flex w-full`}
    >
      {templateStyle === 'a' && (
        <div className="absolute top-0 left-0 h-[100%] w-[100%]">
          <SiteImage src={{ url: '/images/story_background.jpg' }} layout="fill" />
        </div>
      )}
      <h1 className="z-10 mx-[auto] font-nimbus-sans-extd-d text-[35px] font-bold uppercase leading-[90%] md:mx-[auto] md:max-w-[1000px] md:text-[125px]">
        {title}
      </h1>
    </div>
  );

  const landscapeImage = isDesktop
    ? (image && image.width > image.height) ||
      (mobileImage && mobileImage.width > mobileImage.height)
    : (mobileImage && mobileImage.width > mobileImage.height) ||
      (image && image.width > image.height);

  const imageValue = isDesktop ? image || mobileImage : mobileImage || image;

  const imageRender = imageValue && (
    <SiteImage
      src={imageValue}
      className={`w-[300px] ${
        landscapeImage
          ? 'max-h-[300px] md:mx-[auto] md:max-h-[807px] md:w-[100%] md:max-w-[1258px]'
          : 'max-h-[408px] md:max-h-[807px] md:w-[620px]'
      } ${
        templateStyle === 'a'
          ? 'relative mt-[-240px] mb-[70px] ml-[-49px] md:mt-[0] md:ml-[auto] md:mr-[0] md:max-w-[50%]'
          : 'absolute left-0 right-0 bottom-0 mx-[auto] translate-y-1/2 overflow-visible'
      } h-[408px] md:h-[807px]`}
    />
  );

  const richTextOptions = richText => {
    const hyperlink = richText?.links?.entries?.hyperlink || [];
    const getLinkedEntry = linkId => hyperlink.find(l => l?.sys?.id === linkId);

    return {
      renderNode: {
        [BLOCKS.HEADING_1]: (_, children) => (
          <h1
            className={`font-nimbus-sans-extd-d font-bold uppercase leading-[90%]
        
        ${templateStyle === 'b' ? 'text-[35px]' : 'text-[20px] md:text-[30px] '}`}
          >
            {children}
          </h1>
        ),
        [BLOCKS.HEADING_2]: (_, children) => (
          <h2
            className={`font-nimbus-sans-extd-d font-bold uppercase leading-[90%] md:mx-auto md:mt-0 md:max-w-[90%] ${
              templateStyle === 'a'
                ? ' mb-[14px] text-left text-[20px] md:mb-[21px] md:ml-0 md:text-[30px]'
                : 'text-center'
            } ${templateStyle === 'b' && 'text-[20px] md:text-[30px]'}  ${
              templateStyle === 'c' && 'mb-[44px]  text-[20px] md:mb-[53px] md:text-[35px]'
            }`}
          >
            {children}
          </h2>
        ),
        [BLOCKS.HEADING_3]: (_, children) => (
          <h3 className="mb-[22px] mt-[44px] py-0 px-[50px] font-nimbus-sans-extd-d text-[14px] font-bold uppercase leading-[90%]">
            {children}
          </h3>
        ),
        [BLOCKS.PARAGRAPH]: (_, children) => {
          const isEmpty = children.length === 1 && children[0] === '';

          const content = isEmpty ? <br /> : addNewLines(children);

          return (
            <p
              className={`${
                templateStyle === 'a' ? 'pr-[25px] pl-0 md:pr-[150px]' : 'md:px-[228px]'
              } mb-[0] py-0 px-[20px] font-nimbus-sans-d text-[15px] leading-[140%] last:pb-[71px]`}
            >
              {content}
            </p>
          );
        },

        [INLINES.ENTRY_HYPERLINK]: (node, children) => {
          const {
            data: {
              target: {
                sys: { id },
              },
            },
          } = node;

          const link = getLinkedEntry(id);

          if (!link) {
            return children;
          }

          return (
            <SiteLink href={getLinkHref(link)} className="underline">
              {children}
            </SiteLink>
          );
        },
      },
    };
  };

  return (
    <>
      <div className="relative flex w-full flex-col bg-blue text-center text-white">
        {mainHeadingRender}
        <div
          className={`flex md:mx-[auto] ${
            templateStyle === 'a'
              ? 'z-10 ml-[49px] mr-[34px] flex-col-reverse md:mt-[-665px] md:flex-row-reverse md:justify-center'
              : 'ml-[37px] mr-[31px] flex-col'
          }`}
        >
          <div
            className={`${
              templateStyle === 'a'
                ? 'mb-[0] pb-0 text-left md:ml-[92px] md:w-[39%]'
                : 'mb-[170px] text-center md:pb-[240px]'
            } whitespace-pre-wrap md:mx-[auto] md:max-w-[1000px]`}
          >
            <RichText data={article.article} options={richTextOptions(article.article)} />
          </div>
          {imageRender}
        </div>
      </div>
      {templateStyle !== 'a' && imageValue && (
        <div className="min-h-[240px] w-full bg-white md:h-[470px]" />
      )}
    </>
  );
};

export default withPropValidation(Section, ({ data: { name, article } }) =>
  validateRequired({
    Name: name,
    Article: article,
  })
);
